import { cn } from '../../utils.js';

/**
 * One admin area instead of two tabbed pages.
 *
 * Splitting "קליטת נתונים" from "בקרת נתונים" meant the monthly upload and the
 * screens you check it with lived under different top-level links. They are one
 * job, so they are now one place, grouped by what you are trying to do.
 *
 * RTL: `start` is the right-hand side, so the nav sits where the eye begins.
 * Below `lg` it becomes a horizontal strip — a fixed sidebar on a phone would
 * leave almost nothing for the content.
 */
export interface AdminSection {
  id: string;
  label: string;
  /** Small count or alert badge, e.g. open data-quality errors. */
  badge?: number;
  alert?: boolean;
}

export interface AdminGroup {
  title: string;
  sections: AdminSection[];
}

export function AdminSideNav({
  groups,
  active,
  onChange,
}: {
  groups: AdminGroup[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <nav
      aria-label="ניווט ניהול"
      className="flex gap-4 overflow-x-auto pb-2 lg:sticky lg:top-20 lg:h-fit lg:w-56 lg:shrink-0 lg:flex-col lg:overflow-visible lg:pb-0"
    >
      {groups.map((g) => (
        <div key={g.title} className="lg:w-full">
          <p className="mb-1 hidden text-xs font-semibold uppercase tracking-wide text-muted-foreground lg:block">
            {g.title}
          </p>
          <div className="flex gap-1 lg:flex-col">
            {g.sections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onChange(s.id)}
                aria-current={active === s.id ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors lg:w-full lg:justify-between',
                  active === s.id
                    ? 'bg-secondary font-semibold text-foreground'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
              >
                <span>{s.label}</span>
                {s.badge !== undefined && s.badge > 0 && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold tabular-nums',
                      s.alert ? 'bg-destructive/15 text-destructive' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {s.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}
